import { useNavigate, Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { LogOut, Shield, Star, Home, Users, MessageSquare, Bell, Dices, PenTool } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';

/**
 * Mobile Header component for ChatPage
 */
const MobileHeader = ({ user, selectedRoom, mobileNavTab, activeTab, handleLogout, navigateToDashboard }) => {
  const navigate = useNavigate();

  const saveStateAndNavigate = () => {
    sessionStorage.setItem('chatState', JSON.stringify({
      roomId: selectedRoom?.id,
      roomName: selectedRoom?.name,
      mobileNavTab: mobileNavTab,
      activeTab: activeTab
    }));
    navigate('/profile');
  };

  return (
    <div className="md:hidden bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-2 flex items-center justify-between">
      {/* Left: Profile Picture */}
      <button onClick={saveStateAndNavigate} className="flex items-center">
        <Avatar className="w-9 h-9 border-2 border-white/30">
          <AvatarImage src={getImageUrl(user?.avatar_url)} />
          <AvatarFallback className="bg-blue-500 text-white text-sm">
            {user?.username?.substring(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </button>
      
      {/* Logout button */}
      <button 
        onClick={handleLogout}
        className="p-1.5 hover:bg-white/10 rounded-full transition-colors ml-1"
        title="Logout"
      >
        <LogOut className="w-4 h-4" />
      </button>
      
      {/* Center: Logo & Title */}
      <div className="flex items-center gap-2 flex-1 justify-center">
        <img src="/logo.png" alt="ChatHub" className="h-7 w-auto" onError={(e) => e.target.style.display = 'none'} />
        <div className="text-center">
          <h1 className="text-lg font-bold leading-tight">ChatHub India</h1>
          <p className="text-[10px] text-blue-200 leading-tight">Meet People Make Friends</p>
        </div>
      </div>
      
      {/* Right: Dashboard */}
      <div className="flex items-center gap-1">
        {(user.role === 'staff' || user.role === 'super_admin' || user.role === 'premium') && (
          <button 
            onClick={navigateToDashboard}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            title={user.role === 'premium' ? 'Premium Dashboard' : 'Dashboard'}
          >
            {user.role === 'premium' ? (
              <Star className="w-5 h-5 text-amber-300" />
            ) : (
              <Shield className="w-5 h-5" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Mobile Tab Navigation component
 */
export const MobileTabNav = ({ mobileNavTab, setMobileNavTab, setActiveTab, unreadPmCount }) => {
  const tabs = [
    { id: 'rooms', label: 'Rooms', icon: Home },
    { id: 'friends', label: 'Friends', icon: Users },
    { id: 'chats', label: 'Chats', icon: MessageSquare },
    { id: 'ludo', label: 'Ludo', icon: Dices },
    { id: 'notifications', label: 'Alerts', icon: Bell, badge: unreadPmCount },
  ];

  return (
    <nav className="md:hidden bg-white dark:bg-gray-900 border-b dark:border-gray-700 shadow-sm z-40">
      <div className="flex">
        {tabs.map(tab => {
          const className = `flex-1 py-3 px-2 text-center text-sm font-medium transition-colors relative ${
            mobileNavTab === tab.id 
              ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30' 
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
          }`;

          const content = (
            <>
              <tab.icon className="w-5 h-5 mx-auto mb-1" />
              <span className="text-xs">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute top-1 right-1/4 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
            </>
          );

          if (tab.href) {
            return (
              <Link key={tab.id} to={tab.href} className={className}>
                {content}
              </Link>
            );
          }

          return (
            <button
              key={tab.id}
              onClick={() => {
                setMobileNavTab(tab.id);
                if (tab.id === 'rooms') setActiveTab('rooms');
                if (tab.id === 'friends') setActiveTab('friends');
              }}
              className={className}
            >
              {content}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileHeader;
